import axios, { AxiosError } from "axios";
import axiosInstance from "config/axiosConfig";

/**
 * Contractor details as returned by the contractor API
 */
export interface ContractorDetails {
    contractor_name: string;
    membership_number: string;
    member_since: string;
    contractor_mobile_number: string;
    contractor_classification: string;
    banking_points_qtd: number;
    banking_qtd_kgs: number;
    banking_mtd_kgs: number;
    banking_ytd_kgs: number;
    clubid: string;
    clubname: string;
    clublocation: string;
    bd_namer: string;
    bd_mobile_number: string;
    city: string;
    state: string;
    pincode: string;
    loyalty_points: number;
}

interface ContractorResponse {
    data: ContractorDetails;
    metadata: {
        source: string;
    };
}

type ErrorType = {
    error: string;
    message: string;
    status: number;
    timestamp: string;
};

export const API_PATHS = {
    CONTRACTOR_DETAILS: "/contact/contractor-details",
};

/**
 * Get contractor details by phone number
 * @param phoneNumber - 10-digit phone number to look up
 * @returns ContractorDetails if found, null if not found (404)
 * @throws Error for other API errors
 */
export const getContractorDetails = async (
    phoneNumber: string
): Promise<ContractorDetails | null> => {
    try {
        const response = await axiosInstance.get<ContractorResponse>(
            API_PATHS.CONTRACTOR_DETAILS,
            {
                params: { phoneNumber },
            }
        );
        return response.data.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError<ErrorType>;
            // 404 means contractor not found - return null instead of throwing
            if (axiosError.response?.status === 404) {
                return null;
            }
            throw new Error(
                axiosError.response?.data?.message || "Failed to fetch contractor details"
            );
        }
        throw new Error("An unknown error occurred while fetching contractor details");
    }
};
